import { useSearchParams } from "@/lib/router-compat";
import {
  ShieldCheck,
  Clock,
  Brain,
  HeartHandshake,
  Building2,
} from "lucide-react";
import { BcbsilHeroForm } from "./BcbsilHeroForm";
import { getEmployerNameFromParams } from "./employerParam";

/**
 * Conversion-first hero for /bcbs-illinois-saint.
 * Left: Problem → Solution → Benefit framing with employer personalization.
 * Right: Compact eligibility form, above the fold on desktop and mobile.
 * On mobile the form comes FIRST so it's immediately actionable.
 */
export function BcbsilHeroSection() {
  const [searchParams] = useSearchParams();
  const employer = getEmployerNameFromParams(searchParams);

  return (
    <section className="relative overflow-hidden bg-white pt-6 sm:pt-10 pb-14 sm:pb-20">
      {/* Soft brand glow */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-accent/8 blur-3xl" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        {/* Mobile: form first, then copy */}
        <div className="lg:hidden space-y-6">
          <BcbsilHeroForm employerName={employer} />
          <HeroCopy employerName={employer} />
        </div>

        {/* Desktop: two columns, copy left, form right */}
        <div className="hidden lg:grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <HeroCopy employerName={employer} />
          </div>
          <div className="lg:col-span-5">
            <BcbsilHeroForm employerName={employer} />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroCopy({ employerName }: { employerName: string | null }) {
  return (
    <div className="space-y-5 text-left">
      {/* Eyebrow Badge with dynamic employer support */}
      <div className="inline-flex flex-wrap items-center gap-2 px-3.5 py-1.5 rounded-full bg-muted border border-border text-primary text-xs sm:text-sm font-medium">
        <ShieldCheck className="w-4 h-4 text-accent shrink-0" />
        <span>
          {employerName ? (
            <>
              For{" "}
              <strong className="text-primary font-bold">{employerName}</strong>{" "}
              employees &amp; families covered by BCBSIL
            </>
          ) : (
            "For employees and families covered by Blue Cross and Blue Shield of Illinois"
          )}
        </span>
      </div>

      {employerName && (
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md bg-accent/15 text-accent-foreground border border-accent/30">
          <Building2 className="w-3.5 h-3.5 text-accent shrink-0" />
          <span>Recognized Employer Benefit Partner</span>
        </div>
      )}

      {/* H1 — Problem → Solution → Benefit */}
      <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-primary leading-[1.15]">
        When depression hasn&apos;t lifted, there&apos;s still a way forward.
      </h1>

      <p className="text-base sm:text-lg text-foreground leading-relaxed max-w-2xl">
        SAINT® is an FDA-cleared, five-day treatment for adults whose depression
        hasn&apos;t improved with antidepressants.{" "}
        {employerName ? (
          <>
            Through your{" "}
            <strong className="font-semibold text-primary">
              {employerName}
            </strong>{" "}
            Blue Cross and Blue Shield of Illinois plan, it may be available to
            you and the family members on your policy.
          </>
        ) : (
          "Through your BCBSIL plan, it may be available to you and the family members on your plan."
        )}
      </p>

      {/* Trust badges */}
      <div className="flex flex-wrap gap-x-6 gap-y-3 pt-1">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-accent shrink-0" />
          <span className="text-sm font-medium text-primary">
            5 days, not 6–9 weeks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-accent shrink-0" />
          <span className="text-sm font-medium text-primary">
            fMRI-guided, personalized
          </span>
        </div>
        <div className="flex items-center gap-2">
          <HeartHandshake className="w-5 h-5 text-accent shrink-0" />
          <span className="text-sm font-medium text-primary">
            We handle the insurance
          </span>
        </div>
      </div>

      {/* Proof line */}
      <p className="text-sm text-muted-foreground pt-1 leading-relaxed">
        In a controlled clinical trial, 79% of participants reached remission.¹
        Individual results vary.
        <br />
        <span className="text-xs">
          ¹ Cole EJ, et al. <em>American Journal of Psychiatry</em>, 2022.
        </span>
      </p>
    </div>
  );
}
